import { ErrorService } from "@/services/errorService";
import editorWorkerUrl from "./index.ts?worker&url";

let workerInstance: Worker | undefined;

/**
 * Checks if Web Workers are supported in the current environment
 * @returns {boolean} True if Web Workers are supported, false otherwise
 */
function isWebWorkerSupported(): boolean {
  return typeof window.Worker !== "undefined";
}

/**
 * Creates a new EditorWorker instance
 * @returns {Worker | undefined} The EditorWorker instance or undefined if creation fails
 */
function createWorkerInstance(): Worker | undefined {
  try {
    const worker = new Worker(new URL(editorWorkerUrl, import.meta.url), {
      type: "module",
    });

    worker.onerror = (error: ErrorEvent) => {
      ErrorService.report(error);
      worker.terminate();
      workerInstance = undefined;
    };

    return worker;
  } catch (error) {
    ErrorService.report(error);
    return undefined;
  }
}

/**
 * Retrieves or creates an instance of the EditorWorker
 * @returns {Worker | undefined} The EditorWorker instance or undefined if not supported
 */
export function getEditorWorker(canCreateNew: boolean = false): Worker | undefined {
  if (!workerInstance && canCreateNew) {
    if (isWebWorkerSupported()) {
      const newWorker = createWorkerInstance();
      if (newWorker) {
        workerInstance = newWorker;
      } else {
        console.warn("Failed to create EditorWorker. Some features may be limited.");
        return undefined;
      }
    } else {
      console.warn("Web Workers are not supported in this environment. Some features may be limited.");
      return undefined;
    }
  }

  return workerInstance;
}

/**
 * Terminates the EditorWorker instance if it exists
 * @returns {void}
 */
export function terminateEditorWorker(): void {
  if (workerInstance) {
    try {
      workerInstance.terminate();
    } catch (error) {
      ErrorService.report(error);
    } finally {
      workerInstance = undefined;
    }
  }
}

/**
 * Checks if the EditorWorker is currently active
 * @returns {boolean} True if the worker is active, false otherwise
 */
export function isEditorWorkerActive(): boolean {
  return workerInstance != null;
}
